/**
 * Missing Translation Handler
 *
 * Handles cases where a translation key is not found.
 * In development, logs warnings to help developers identify missing translations.
 * In production, silently returns the key.
 *
 * ## Usage
 * This handler is automatically used by TranslateService when a key is not found.
 * You don't need to call it directly.
 *
 * ## Finding Missing Translations
 * 1. Run the app in development mode
 * 2. Check browser console for "[i18n] Missing translation:" warnings
 * 3. Add missing keys to the translation system via admin panel
 */

import { Injectable, isDevMode } from '@angular/core';
import { MissingTranslationHandler, MissingTranslationHandlerParams } from '@ngx-translate/core';

@Injectable()
export class AppMissingTranslationHandler implements MissingTranslationHandler {
  /** Track reported missing keys to avoid spamming console */
  private reportedKeys = new Set<string>();

  handle(params: MissingTranslationHandlerParams): string {
    const key = params.key;

    // Only log in development mode
    if (isDevMode()) {
      // Only report each key once per session
      if (!this.reportedKeys.has(key)) {
        this.reportedKeys.add(key);
        console.warn(`[i18n] Missing translation: "${key}"`);

        // Additional debug info for complex keys
        if (params.interpolateParams) {
          console.warn(`[i18n]   with params:`, params.interpolateParams);
        }
      }
    }

    // Return the key itself as fallback
    // This makes it obvious in the UI that a translation is missing
    return key;
  }

  /**
   * Get all missing keys reported this session
   * Useful for debugging and reporting
   */
  getReportedMissingKeys(): string[] {
    return Array.from(this.reportedKeys);
  }

  /**
   * Clear the reported keys set
   * Useful when switching languages to re-report missing keys
   */
  clearReportedKeys(): void {
    this.reportedKeys.clear();
  }
}
